import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectionPositionPair,
  ScrollStrategy,
  ScrollStrategyOptions
} from '@angular/cdk/overlay';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { ModalType } from '../modal/modal.enum';
import { DOCUMENT, isPlatformBrowser, NgClass, NgTemplateOutlet } from '@angular/common';
import { BrandsEnum } from '../../../_enums/brands.enum';
import { PositionEnum } from '../../../_enums/position.enum';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { SdThemeWrapperComponent } from '../../theme-wrapper/theme-wrapper.component';
import { CoreModalService } from './core-modal.service';

/** @dynamic */
@Component({
  selector: 'sd-core-modal, [sd-core-modal]',
  templateUrl: './core-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('isOpenedChanged', [
      transition(
        'void => *',
        [
          style({
            transform: 'translate{{translate_axis}}({{translate_value}})'
          }),
          animate(
            '0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
            style({ transform: 'translate{{translate_axis}}(0%)' }),
          )
        ],
        { params: { translate_axis: 'X' } },
      ),
      transition(
        '* => void',
        [
          animate(
            '0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
            style({
              transform: 'translate{{translate_axis}}({{translate_value}})'
            }),
          )
        ],
        { params: { translate_axis: 'X' } },
      )
    ])
  ],
  standalone: true,
  imports: [
    CdkConnectedOverlay,
    SdThemeWrapperComponent,
    NgClass,
    CdkTrapFocus,
    NgTemplateOutlet
  ],
  providers: [CoreModalService]
})
export class SdCoreModalComponent implements OnInit, AfterViewInit {
  constructor(
    private scrollStrategyOptions: ScrollStrategyOptions,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  bodyChildren;
  scrollStrategy: ScrollStrategy;
  newPosition: ConnectionPositionPair[];
  overlayPush: boolean;

  @Input()
  brand: BrandsEnum;

  @ViewChild('cdkConnectedOverlayTemp', { read: CdkConnectedOverlay })
  cdkConnectedOverlayTemp: CdkConnectedOverlay;

  @Input()
  disabledAnimation = true;

  @Input()
  overlayOrigin: CdkOverlayOrigin;

  @Input()
  modalBackDrop = false;

  @Input()
  modalClickToClose = true;

  @Input()
  modalType: string;

  @Input()
  modalCustomClass = '';

  @Input()
  modalCustomId = '';

  @Input()
  isOpened = false;

  @Output()
  isOpenedChange = new EventEmitter<boolean>();

  @Input()
  focus = true;

  @Input()
  modalPosition:
    | PositionEnum.left
    | PositionEnum.top
    | PositionEnum.right
    | PositionEnum.bottom
    | PositionEnum.bottomLeft = PositionEnum.bottom;

  getPosition(modalPosition: PositionEnum) {
    switch (modalPosition) {
      case PositionEnum.top:
        this.newPosition = [
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom'
          }
        ];
        break;
      case PositionEnum.left:
        this.newPosition = [
          {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center'
          }
        ];
        break;
      case PositionEnum.right:
        this.newPosition = [
          {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center'
          }
        ];
        break;
      case PositionEnum.bottomLeft:
        this.newPosition = [
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
          }
        ];
        break;
      case PositionEnum.bottomRight:
        this.newPosition = [
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top'
          }
        ];
        break;
      default:
        this.newPosition = [
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top'
          }
        ];
    }
  }

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.getPosition(this.modalPosition);
      this.overlayPush = this.modalType === ModalType.aside;

      if (this.modalType === ModalType.modal || this.modalType === ModalType.aside) {
        this.scrollStrategy = this.scrollStrategyOptions.block();
      } else {
        this.scrollStrategy = this.scrollStrategyOptions.reposition({
          autoClose: true
        });
      }
    }
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {
      this.bodyChildren = this.document.body.children;
    }
  }

  setTranslateAxis(x: PositionEnum) {
    this.isOpenedChange.emit(this.isOpened);
    if (x === PositionEnum.top) {
      return {
        value: '',
        params: {
          translate_axis: 'Y',
          translate_value: '-100%'
        }
      };
    } else if (x === PositionEnum.left) {
      return {
        value: '',
        params: {
          translate_axis: 'X',
          translate_value: '-100%'
        }
      };
    } else if (x === PositionEnum.right) {
      return {
        value: '',
        params: {
          translate_axis: 'X',
          translate_value: '100%'
        }
      };
    } else if (x === PositionEnum.bottom) {
      return {
        value: '',
        params: {
          translate_axis: 'Y',
          translate_value: '100%'
        }
      };
    } else {
      return {
        value: '',
        params: {
          translate_axis: 'X',
          translate_value: '100%'
        }
      };
    }
  }

  connectedOverlayDetach(): void {
    this.isOpened = false;
    this.isOpenedChange.emit(false);
    for (const child of this.bodyChildren) {
      child.removeAttribute('aria-hidden');
    }
  }

  onBackdropClick() {
    if (this.modalClickToClose) {
      this.isOpened = false;
      this.isOpenedChange.emit(false);
    }
  }

  /**
   * Returns null to display the default angular CDK backdrop if the modal has a backdrop.
   * otherwise makes it invisible to still manage the clicks well
   */
  backDropClass(): string {
    if (this.modalBackDrop) {
      // Default style
      return null;
    } else {
      if (this.modalClickToClose) {
        return 'is-invisible';
      } else {
        // If the click does not close the modal and the backdrop is invisible
        return 'is-unclosable';
      }
    }
  }

  /**
   * Allow to update overlayRef position
   */
  updatePosition() {
    if (this.cdkConnectedOverlayTemp?.overlayRef) {
      this.cdkConnectedOverlayTemp.overlayRef.updatePosition();
    }
  }
}
